import { clsx } from 'clsx';
import type { CurrentUser } from 'hooks/useCurrentUser';
import type React from 'react';
import { NavLink as OriginalNavLink } from 'react-router';
import BellIcon from 'turnip/assets/images/bell-icon.svg?react';
import CarrotIcon from 'turnip/assets/images/carrot-icon-white.svg?react';
import PhoneIcon from 'turnip/assets/images/phone-icon.svg?react';
import { isUrl } from 'turnip/Link/isUrl';
import { Stack } from 'turnip/Stack/Stack';
import { carrotDomain } from 'utils/carrotDomain';
import { routes } from 'utils/routes';
import {
  agencyUrl,
  appUrl,
  carrotCrmUrl,
  communityUrl,
  helpUrl,
  marketplaceUrl,
} from 'utils/carrotUrls';
import { AccountSwitcher } from './AccountSwitcher/AccountSwitcher';
import { backgroundClasses } from './backgroundClasses';
import { MainMenu } from './MainMenu/MainMenu';
import { UserMenu } from './UserMenu/UserMenu';

export function AuthenticatedNavBar({
  accounts,
  activeAccountId,
  avatarUrl,
  agencyAccountId,
}: {
  accounts: CurrentUser['accounts'];
  activeAccountId: number | undefined;
  avatarUrl: CurrentUser['avatarUrl'];
  /** If the user has an agency account, this is the agency's account ID */
  agencyAccountId?: number;
}) {
  const domain = carrotDomain();
  const mainItems = menuItems(domain);

  const isAgencyUser = !!agencyAccountId;
  const accountLinkText = isAgencyUser ? 'Agency' : 'Account';
  const accountUrl = isAgencyUser ? agencyUrl : `${appUrl}/account/`;

  const secondaryItems: NavItems = [
    { name: 'Carrot CRM', url: carrotCrmUrl },
    { name: 'Keyword Explorer', url: '/keyword-explorer' },
    {
      name: 'Community',
      url: communityUrl,
    },
  ];

  const mobileItems = [
    { name: accountLinkText, url: accountUrl },
    ...secondaryItems,
    ...mainItems,
  ];

  return (
    <nav
      className="exclude-a11y-check flex h-[50px] justify-between bg-orange-500 text-white md:h-[55px]"
      aria-label="General navigation"
    >
      <Stack direction="horizontal">
        <AccountLink href={accountUrl} isAgencyUser={isAgencyUser} />
        <MobileMenu items={mobileItems} />
        {Number(accounts?.length) >= 2 && (
          <AccountSwitcher
            accounts={accounts}
            activeAccountId={activeAccountId}
          />
        )}
        <SitesLink accountUrl={accountUrl} />
      </Stack>

      <Stack direction="horizontal">
        <MainNavItems items={secondaryItems} visibleBreakpoint="xl" />
        <MainNavItems items={mainItems} visibleBreakpoint="md" />
        <PhoneSupportLink domain={domain} />
        <ProductUpdatesLink />
        <UserMenu avatarUrl={avatarUrl} agencyAccountId={agencyAccountId} />
      </Stack>
    </nav>
  );
}

type NavItems = Array<{ name: string; url: string; condition?: boolean }>;

interface NavLinkProps extends React.ComponentPropsWithoutRef<'a'> {
  children: React.ReactNode;
  screenReaderText?: string;
  href: string;
}
function NavLink({
  href,
  children,
  title,
  id,
  screenReaderText,
}: NavLinkProps) {
  const linkProps = {
    title,
    id,
    className: `flex flex-1 flex-col justify-center whitespace-nowrap px-2 text-white md:px-3 ${backgroundClasses()}`,
  };
  const linkContent = (
    <>
      {screenReaderText && <span className="sr-only">{screenReaderText}</span>}
      {children}
    </>
  );

  if (isUrl(href)) {
    return (
      <a href={href} {...linkProps}>
        {linkContent}
      </a>
    );
  }

  return (
    <OriginalNavLink
      to={href}
      {...linkProps}
      className={({ isActive }) =>
        clsx(linkProps.className, isActive && 'underline underline-offset-4')
      }
    >
      {linkContent}
    </OriginalNavLink>
  );
}

function AccountLink({
  href,
  isAgencyUser,
}: {
  href: string;
  isAgencyUser: boolean;
}) {
  const screenReaderText = isAgencyUser ? 'Visit agency home' : 'Visit account';

  return (
    <div className="hidden md:flex">
      <NavLink href={href} screenReaderText={screenReaderText}>
        <CarrotIcon className="h-[39px]" />
      </NavLink>
    </div>
  );
}

function MobileMenu({ items }: { items: NavItems }) {
  return (
    <div className="flex md:hidden">
      <MainMenu items={items} />
    </div>
  );
}

function SitesLink({ accountUrl }: { accountUrl: string }) {
  return (
    <div className="flex flex-col justify-center border-l-2 border-solid border-[#ee8408]">
      <NavLink href={accountUrl}>
        <span className="text-lg font-bold md:text-[22px]">Sites</span>
      </NavLink>
    </div>
  );
}

const visibleClasses = {
  md: 'md:flex',
  lg: 'lg:flex',
  xl: 'xl:flex',
};
function MainNavItems({
  items,
  visibleBreakpoint,
}: {
  items: NavItems;
  visibleBreakpoint: keyof typeof visibleClasses;
}) {
  return (
    <div className={`hidden ${visibleClasses[visibleBreakpoint]}`}>
      {items.map(({ name, url }) => (
        <NavLink key={url} href={url}>
          {name}
        </NavLink>
      ))}
    </div>
  );
}

function PhoneSupportLink({ domain }: { domain: string }) {
  const assistiveText = 'Schedule a support call';

  return (
    <div className="hidden md:flex">
      <NavLink
        href={`https://help.${domain}/call/`}
        title={assistiveText}
        screenReaderText={assistiveText}
      >
        <PhoneIcon />
      </NavLink>
    </div>
  );
}

function ProductUpdatesLink() {
  return (
    <NavLink
      href="https://app.getbeamer.com/carrot/en"
      id="nav-bar-carrot-updates"
      screenReaderText="View product updates"
    >
      <BellIcon />
    </NavLink>
  );
}

function menuItems(domain: string): NavItems {
  const mainItems: NavItems = [
    { name: 'Call Tracking', url: routes().callTracking },
    { name: 'Trainings', url: `https://members.${domain}` },
    { name: 'Marketplace', url: marketplaceUrl },
    { name: 'Help', url: helpUrl },
  ];

  return mainItems;
}
